import type { SdkPayments, SdkPaymentsInstance } from '@yuno-payments/sdk-web-types'

// Origins the Web SDK talks to on the checkout path, in production spelling
// (the package maps env 'prod' to an empty environment suffix):
//   sdk-web.y.uno — main.js plus every lazy chunk the SDK pulls after boot
//                   (seamless-checkout, apple-pay-controller, …). Fetched by a
//                   plain <script>, so no crossorigin: a preconnect carrying one
//                   warms the CORS pool the script will not use.
//   api.y.uno     — the REST calls behind Yuno.initialize (initConfig) and
//                   mountSeamlessCheckout (get_settings). Reached by fetch under
//                   CORS, so the preconnect must be anonymous or the browser
//                   opens a second connection anyway and the hint buys nothing.
// The card-form and 3DS micro-apps sit on further hosts (see Yuno's white-label
// proxy doc, which lists sdk-web-card.* and sdk-3ds.*), but their production
// hostnames are not confirmed anywhere we can check from here — a wrong host
// spends a connection slot for nothing, so they are left out rather than
// guessed. Confirm them from a checkout waterfall before adding.
export const YUNO_ORIGINS: { href: string; anonymous: boolean }[] = [
  { href: 'https://sdk-web.y.uno', anonymous: false },
  { href: 'https://api.y.uno', anonymous: true },
]

let yunoSdkPromise: Promise<SdkPayments> | null = null

// The SDK bundle the npm loader injects. The package pins this internally
// (`LATEST_VERSION` in dist/bundle.esm.js) and exports only `loadScript`, and its
// own npm version (8.x) is unrelated to the bundle version — so unlike Primer's
// shim this cannot be derived from package.json and has to be mirrored here.
// Re-check it against `LATEST_VERSION` when bumping @yuno-payments/sdk-web.
export const YUNO_SDK_CDN_URL = 'https://sdk-web.y.uno/v1.11/main.js'

// Both mirrored from the package's loader. The bundle assigns `window.SdkPayments`
// and dispatches READY on window as it executes; the loader tags its <script> with
// SCRIPT_ID, which is also what we have to clear below.
const SCRIPT_ID = 'sdk-payments-script'
const READY_EVENT = 'sdk-payments-ready'

const getGlobalSdkPayments = (): SdkPayments | null => {
  const g = (window as { SdkPayments?: SdkPayments }).SdkPayments
  return g && typeof g.initialize === 'function' ? g : null
}

// Same job as the package's `loadScript()`, minus the module fetch. Deliberately no
// `integrity`/`crossorigin`: the package sets those only when called with `sri`, which
// we do not, and the preconnect in YUNO_ORIGINS is non-anonymous to match.
const loadFromCdn = (): Promise<SdkPayments> =>
  new Promise<SdkPayments>((resolve, reject) => {
    const fromGlobal = getGlobalSdkPayments()
    if (fromGlobal) {
      resolve(fromGlobal)
      return
    }

    // The package's loader does NOT remove its <script> when it errors, so a failed
    // attempt leaves a dead tag holding the id. The global being unset proves that tag
    // never executed — drop it instead of adopting it and waiting on a ready event that
    // will never come.
    document.getElementById(SCRIPT_ID)?.remove()

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = YUNO_SDK_CDN_URL

    // For a tag we own, `onload` fires after the bundle has executed, so it normally
    // settles this first. The listener only covers a bundle that finishes later —
    // resolving an already-settled promise is a no-op.
    window.addEventListener(
      READY_EVENT,
      () => {
        const sdk = getGlobalSdkPayments()
        if (sdk) resolve(sdk)
      },
      { once: true },
    )

    script.onload = () => {
      const sdk = getGlobalSdkPayments()
      if (sdk) {
        resolve(sdk)
        return
      }
      // Reject rather than wait: a pending promise hangs the checkout on a spinner,
      // whereas a rejection clears the singleton and the next call retries.
      script.remove()
      reject(new Error('Yuno SDK script loaded but window.SdkPayments is missing'))
    }
    script.onerror = () => {
      // Drop the failed tag so a retry injects a fresh one instead of stacking dead
      // elements — and so the branch above does not adopt it.
      script.remove()
      reject(new Error(`Failed to load Yuno SDK from CDN: ${script.src}`))
    }

    document.head.appendChild(script)
  })

// Yuno distributes its web SDK by CDN only, so the npm package is a thin loader:
// `loadScript()` injects the official <script> and resolves `window.SdkPayments`
// once it has executed. One call therefore downloads, runs and resolves the SDK
// — there is no second bundle to warm separately the way Primer's shim has.
//
// A rejected load is never cached: the singleton resets so the next call (the
// integration's online retry, the checkout mount, a reinit) re-attempts. This
// was already the right behaviour when the mount was the only caller, but the
// pre-warm makes it load-bearing — it moves the first load to the email step,
// where a visitor who is briefly offline is a real scenario.
//
// Clearing the singleton is not enough on its own, though. Retrying the import
// re-reads the page's module map, and Chrome and Firefox cache FAILED module
// fetches there (fixed in the HTML spec 2026-07, whatwg/html#10327, not yet
// shipped in any engine), so the retry rejects instantly without touching the
// network — one cancelled fetch anywhere in the funnel left the checkout unable
// to render a payment form for the rest of the page's life. Script tags are
// exempt from that cache, so when the import fails we inject the same CDN bundle
// the package would have injected. Same reasoning as primerSdk.ts.
export const loadYunoSdk = (): Promise<SdkPayments> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Yuno SDK requires a browser environment'))
  }
  if (!yunoSdkPromise) {
    yunoSdkPromise = import('@yuno-payments/sdk-web')
      .then((m) => m.loadScript())
      .catch((importErr) => {
        console.warn(
          '[yuno] SDK loader chunk failed — injecting the CDN bundle directly',
          importErr,
        )
        return loadFromCdn()
      })
      .catch((err) => {
        // Both paths failed (still offline). Clear the singleton so the next call
        // re-attempts the whole chain.
        yunoSdkPromise = null
        throw err
      })
  }
  return yunoSdkPromise
}

// One SDK instance per page session, shared by every YunoCheckout mount.
//
// This exists to bound a leak inside the SDK. `Yuno.initialize()` builds a fresh YunoConfig that
// subscribes to the SDK's module-level event emitter (PAYMENT_SUBMIT_ONE_TIME_TOKEN_FORM,
// PAYMENT_START, PAYMENT_STATUS, ...), and the SDK exposes NO teardown for it — the only unmount
// methods on the instance are for external buttons and secure fields, there is no destroy(). So
// every initialize adds listeners that are never released.
//
// `yuno` used to be a closure variable on each useYuno instance, so navigating away and back created
// a new composable, saw a null instance, and initialized again: one more set of listeners per
// navigation, hence `MaxListenersExceededWarning` after ~11 round trips, and stale configs still
// subscribed to submit events. Holding it at module scope reduces that to one initialize per page
// session — or per genuinely-required re-initialize, below.
//
// Repeated `startSeamlessCheckout` on one instance is already how the existing reinit path works
// (reinitCheckout re-renders without re-initializing), so cross-mount reuse is the same operation.
let instanceKey = ''
let instanceSpent = false
let instancePromise: Promise<SdkPaymentsInstance> | null = null

// A spent or wrong-key instance is ABANDONED, not destroyed — the SDK gives us no way to release it,
// so its listeners stay. That is the residual leak, now bounded to one per payment attempt or key
// change instead of one per navigation.
export const getYunoInstance = (publicApiKey: string): Promise<SdkPaymentsInstance> => {
  if (instancePromise && !instanceSpent && instanceKey === publicApiKey) return instancePromise

  instanceKey = publicApiKey
  instanceSpent = false
  instancePromise = loadYunoSdk()
    .then((Yuno) => Yuno.initialize(publicApiKey))
    .catch((error) => {
      // Never cache a rejection: the next mount must retry rather than inherit a dead promise. Same
      // rule as loadYunoSdk, and load-bearing for the same reason — a briefly-offline visitor would
      // otherwise have a permanently dead checkout.
      if (instanceKey === publicApiKey) instancePromise = null
      throw error
    })

  return instancePromise
}

// A failed payment SPENDS the instance: the SDK's ApplePayController latches `isExecuted` and its
// handler then silently no-ops, and `PaymentRetry.isTriggeredByExternalButton` is never reset. See
// YUNO_CHECKOUT.md. Re-initializing is the only reset the SDK offers, so mark it here and the next
// getYunoInstance builds a fresh one. Must live with the instance rather than in useYuno: the flag
// has to survive the remount that follows onCheckoutFail, or the new mount would reuse the spent
// instance and reproduce the dead-Apple-Pay-button bug.
export const markYunoInstanceSpent = (): void => {
  instanceSpent = true
}
